import { localized, msg } from '@lit/localize';
import { Task as LitTask } from '@lit/task';
import { html, LitElement, unsafeCSS } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import './app';
import './auth-page';
import './error-boundary';
import '@spectrum-web-components/theme/sp-theme.js';
import '@spectrum-web-components/theme/spectrum-two/scale-medium.js';
import '@spectrum-web-components/theme/spectrum-two/scale-large.js';
import '@spectrum-web-components/theme/spectrum-two/theme-light.js';
import '@spectrum-web-components/theme/spectrum-two/theme-dark.js';
import type { User } from '../api/types';
import { environment } from '../config/environment';
import {
  getLocale,
  type LocalePreference,
  persistLocalePreference,
  readLocalePreference,
  resolveLocalePreference,
  setLocale,
} from '../i18n/localization';
import { setSentryUser } from '../sentry';
import componentColors from '../styles/component-colors.css?raw';

@customElement('sb-root')
@localized()
export class SbRoot extends LitElement {
  static styles = unsafeCSS(componentColors);
  private static readonly THEME_VARS_TO_SYNC = [
    '--color-app-bg',
    '--color-scrollbar-thumb',
  ] as const;

  @state()
  accessor theme: 'light' | 'dark' | 'system' = 'system';

  @state()
  accessor localePreference: LocalePreference = 'system';

  private mql: MediaQueryList = window.matchMedia(
    '(prefers-color-scheme: dark)'
  );

  private get effectiveColor(): 'light' | 'dark' {
    return this.theme === 'system'
      ? this.mql.matches
        ? 'dark'
        : 'light'
      : this.theme;
  }

  private get effectiveLocale() {
    return resolveLocalePreference(this.localePreference);
  }

  private authTask = new LitTask(this, {
    task: async (): Promise<User | false> => {
      // Construct API URL - handle both full URLs (production) and relative paths (development)
      let apiBase = environment.apiBaseUrl;
      if (!apiBase.endsWith('/')) {
        apiBase = `${apiBase}/`;
      }
      const base = apiBase.startsWith('http')
        ? new URL(apiBase)
        : new URL(apiBase, window.location.origin);

      const meUrl = new URL('auth/me', base);
      const res = await fetch(meUrl, { credentials: 'include' });

      if (res.status === 401) {
        return false;
      }

      return (await res.json()) as User;
    },
    args: () => [], // empty array => run once after first update
  });

  private async logout() {
    try {
      let apiBase = environment.apiBaseUrl;
      if (!apiBase.endsWith('/')) {
        apiBase = `${apiBase}/`;
      }
      const base = apiBase.startsWith('http')
        ? new URL(apiBase)
        : new URL(apiBase, window.location.origin);
      const signoutUrl = new URL('auth/signout', base);
      signoutUrl.searchParams.set('lang', this.effectiveLocale);
      signoutUrl.searchParams.set('theme', this.effectiveColor);
      window.location.href = signoutUrl.toString();
    } catch (err) {
      console.error('Failed to sign out', err);
    }
  }

  render() {
    return this.authTask.render({
      pending:
        () => html`<div style="display: flex; align-items: center; justify-content: center; height: 100vh;">
        <p>${msg('Loading...')}</p>
      </div>`,
      complete: (user) => {
        if (user) {
          setSentryUser({
            id: user.id,
            email: user.email || undefined,
            name: user.name || undefined,
          });
        }

        return user
          ? html`
              <sb-error-boundary>
                <sp-theme
                  system="spectrum-two"
                  color=${this.effectiveColor}
                  scale="medium"
                  @theme-change=${this.onThemeChange}
                  @locale-change=${this.onLocaleChange}
                >
                  <sb-app
                    .user=${user}
                    @logout=${this.logout}
                    .theme=${this.theme}
                    .localePreference=${this.localePreference}
                  ></sb-app>
                </sp-theme>
              </sb-error-boundary>
            `
          : html`
              <sp-theme
                system="spectrum-two"
                color=${this.effectiveColor}
                scale="medium"
                @theme-change=${this.onThemeChange}
                @locale-change=${this.onLocaleChange}
              >
                <kv-auth-page
                  .theme=${this.theme}
                  .localePreference=${this.localePreference}
                  color=${this.effectiveColor}
                ></kv-auth-page>
              </sp-theme>
            `;
      },
      error: (
        error
      ) => html`<div style="display: flex; align-items: center; justify-content: center; height: 100vh;">
        <p>${msg('Error')}: ${error instanceof Error ? error.message : String(error) || msg('Unknown error')}</p>
      </div>`,
    });
  }

  connectedCallback(): void {
    super.connectedCallback();
    const stored = localStorage.getItem('theme');
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      this.theme = stored;
    }
    this.localePreference = readLocalePreference();
    void this.applyLocale();
    this.mql.addEventListener('change', this.onSystemThemeChange);
    window.addEventListener('languagechange', this.onBrowserLanguageChange);
  }

  disconnectedCallback(): void {
    this.mql.removeEventListener('change', this.onSystemThemeChange);
    window.removeEventListener('languagechange', this.onBrowserLanguageChange);
    super.disconnectedCallback();
  }

  protected updated(): void {
    void this.syncGlobalThemeVars();
  }

  private async syncGlobalThemeVars(): Promise<void> {
    const themeEl = this.renderRoot?.querySelector('sp-theme') as
      | (HTMLElement & { updateComplete?: Promise<unknown> })
      | null;
    if (!themeEl) return;
    try {
      await themeEl.updateComplete;
    } catch (_) {
      // no-op: best-effort sync
    }
    if (!this.isConnected) return;
    this.syncCssVarsFromElement(
      themeEl,
      document.documentElement,
      SbRoot.THEME_VARS_TO_SYNC
    );
  }

  private syncCssVarsFromElement(
    source: HTMLElement,
    target: HTMLElement,
    vars: readonly string[]
  ): void {
    const sourceStyle = getComputedStyle(source);
    for (const cssVar of vars) {
      const value = sourceStyle.getPropertyValue(cssVar).trim();
      if (!value) {
        target.style.removeProperty(cssVar);
        continue;
      }
      target.style.setProperty(cssVar, value);
    }
  }

  private onSystemThemeChange = () => {
    if (this.theme === 'system') {
      this.requestUpdate();
    }
  };

  private onBrowserLanguageChange = () => {
    if (this.localePreference === 'system') {
      void this.applyLocale();
    }
  };

  private onThemeChange(
    e: CustomEvent<{ value: 'light' | 'dark' | 'system' }>
  ) {
    const next = e.detail?.value;
    if (next) {
      this.theme = next;
      localStorage.setItem('theme', next);
    }
  }

  private onLocaleChange(e: CustomEvent<{ value: LocalePreference }>) {
    const next = e.detail?.value;
    if (next) {
      this.localePreference = next;
      persistLocalePreference(next);
      void this.applyLocale();
    }
  }

  private async applyLocale(): Promise<void> {
    const next = this.effectiveLocale;
    document.documentElement.lang = next;
    if (getLocale() === next) return;
    await setLocale(next);
  }
}
